/**
 * LegalAgreementsForm - Professional acknowledgment and legal agreements
 *
 * 3 checkboxes:
 * 1. Professional acknowledgment (mandatory)
 * 2. Terms & Privacy (mandatory)
 * 3. Marketing opt-in (optional)
 *
 * Saves to user_metadata.agreements_v1 with timestamp and version
 */

import { useState } from 'react';
import { Checkbox } from '../shared/Checkbox';
import { useAgreements } from '../../hooks/useAgreements';
import { useRole } from '../../context/RoleContext';

interface LegalAgreementsFormProps {
  onComplete: () => void;
  onBack?: () => void;
}

export function LegalAgreementsForm({ onComplete, onBack }: LegalAgreementsFormProps) {
  const [agreements, setAgreements] = useState({
    professionalAcknowledgment: false,
    termsAndPrivacy: false,
    marketingOptIn: false,
  });

  const { saveAgreements, isLoading, error } = useAgreements();
  const { markAgreementsAsAccepted } = useRole();

  const allMandatoryChecked =
    agreements.professionalAcknowledgment && agreements.termsAndPrivacy;

  const handleSubmit = async () => {
    if (!allMandatoryChecked || isLoading) return;

    // Send simple structure to hook - it will transform to DB structure
    const success = await saveAgreements({
      timestamp: new Date().toISOString(),
      version: '1.0',
      professionalAcknowledgment: agreements.professionalAcknowledgment,
      termsAndPrivacy: agreements.termsAndPrivacy,
      marketingOptIn: agreements.marketingOptIn,
    });

    if (success) {
      markAgreementsAsAccepted();
      onComplete();
    }
  };

  return (
    <div className="legal-agreements-container">
      <div className="legal-agreements-form">
        <h2>Professional Acknowledgment</h2>
        <p>Please review and accept the following to continue:</p>

        <div className="checkbox-group">
          <Checkbox
            checked={agreements.professionalAcknowledgment}
            onChange={(checked) =>
              setAgreements({ ...agreements, professionalAcknowledgment: checked })
            }
            required
          >
            I represent that I am a qualified professional, and I accept full
            responsibility for the clinical application of this tool and its settings
            during my sessions.
          </Checkbox>

          <Checkbox
            checked={agreements.termsAndPrivacy}
            onChange={(checked) =>
              setAgreements({ ...agreements, termsAndPrivacy: checked })
            }
            required
          >
            I accept the{' '}
            <a
              href="https://emdrsync.io/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href="https://emdrsync.io/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </Checkbox>

          <Checkbox
            checked={agreements.marketingOptIn}
            onChange={(checked) =>
              setAgreements({ ...agreements, marketingOptIn: checked })
            }
          >
            I agree to receive occasional updates via email.
          </Checkbox>
        </div>

        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
            <p className="error-hint">Please try again or contact support if the issue persists.</p>
          </div>
        )}

        <button
          className="primary-button"
          onClick={handleSubmit}
          disabled={!allMandatoryChecked || isLoading}
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </button>

        {onBack && <button
          onClick={onBack}
          disabled={isLoading}
          style={{
            width: '100%',
            marginTop: '12px',
            padding: '12px 20px',
            background: 'transparent',
            color: '#6B7280',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9375rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Back
        </button>}
      </div>
    </div>
  );
}
